import { Box, Button, Divider, Flex, Spacer, useToast, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { MeetingTypes } from '@llx/common';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CreateMeetingInputDto,
  MeetingFieldsFragment,
  MeetingRelationCaseClusterFragment,
  MeetingRelationClientsFragment,
  MeetingRelationTeamsFragment,
  UpdateMeetingInputDto,
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
} from '@/@generated/codegen/API';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import useMeetingUserAccessForMeeting from '@/hooks/useMeetingUserAccessForMeeting';
import { DEFAULT_HOST_NAME, DEFAULT_HOST_VALUE } from '@/utils/constants/app.constants';
import { IsLocalHost } from '@/utils/helpers/isLocalHost';

import { CreateMeetingFormColumnOne } from './components/CreateMeetingFormColumnOne';
import { CreateMeetingFormColumnTwo } from './components/CreateMeetingFormColumnTwo';
import { CreateMeetingFormData, createMeetingFormSchema } from './createMeetingFormSchema';

type Props = {
  // NOTE: if we have a meeting, we are in edition mode or in duplicate mode
  meeting?: MeetingFieldsFragment &
    MeetingRelationClientsFragment &
    MeetingRelationTeamsFragment &
    MeetingRelationCaseClusterFragment;
  isDuplicateMeeting?: boolean;
  closeMeetingForm: () => void;
};

export const CreateMeetingForm = ({ meeting, isDuplicateMeeting, closeMeetingForm }: Props) => {
  const { t } = useTranslation();
  const { userID } = useLoggedInUserAuthContext();
  const { userAccessList } = useMeetingUserAccessForMeeting(meeting?.id);
  const [mutationCreateMeeting] = useCreateMeetingMutation();
  const [mutationUpdateMeeting] = useUpdateMeetingMutation();

  const toast = useToast();
  const [isColumnOneValid, setFirstColumnValid] = useState<boolean>(false);
  const form = useForm<CreateMeetingFormData>({
    resolver: zodResolver(createMeetingFormSchema),
    defaultValues: {
      meetingType: (meeting?.meetingType as MeetingTypes) || MeetingTypes.LIVE,
      name: meeting?.name || '',
      eventDate: meeting?.eventDate ? new Date(meeting.eventDate) : new Date(),
      location: meeting?.eventLocation || '',
      selectedClientID: undefined,
      meetingClientIDs: meeting?.clients.edges.map((el) => el.node.id) || [],

      selectedTeamID: undefined,
      meetingTeamIDs: meeting?.teams.edges.map((el) => el.node.id) || [],
      caseClusterID: meeting?.caseClusterId || '',
      hostUserID: meeting?.hostId || (IsLocalHost() ? userID : DEFAULT_HOST_VALUE),
      hostFullName: meeting?.hostFullName || (IsLocalHost() ? DEFAULT_HOST_NAME : ''),
      purchaseOrderNbr: meeting?.purchaseOrderNbr || (IsLocalHost() ? 'PO-111' : ''),
      customNotes: meeting?.meetingNotes || '',
      streamUrl: meeting?.streamUrl || '',
      isDemo: meeting?.isDemo || false,
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const onSubmit = useCallback(
    async (values: CreateMeetingFormData) => {
      if (values.meetingType === MeetingTypes.LIVE && values.meetingClientIDs.length === 0) {
        toast({
          title: 'Error',
          description: t('admin.createMeeting.form.meetingClients.live.error'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      if (values.meetingType === MeetingTypes.LIVE && values.meetingTeamIDs?.length === 0) {
        toast({
          title: 'Error',
          description: t('admin.createMeeting.form.meetingTeams.live.error'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (!meeting || isDuplicateMeeting) {
        const meetingInput: CreateMeetingInputDto = {
          name: values.name,
          caseClusterId: values.caseClusterID,
          hostId: values.hostUserID,
          hostFullName: values.hostFullName,
          eventDate: values.eventDate,
          eventLocation: values.location,
          clientIds: values.meetingClientIDs,
          purchaseOrderNbr: values.purchaseOrderNbr || '',
          meetingNotes: values.customNotes || '',
          isMock: false,
          isInTesting: false,
          isArchived: false,
          meetingType: values.meetingType,
          isDemo: values.isDemo,
          streamUrl: values.streamUrl,
          teamIds: values.meetingTeamIDs,
        };
        await mutationCreateMeeting({
          variables: {
            data: meetingInput,
          },
        }).finally(() => {
          closeMeetingForm();
        });
      } else {
        const meetingUpdate: UpdateMeetingInputDto = {
          id: meeting.id,
          name: values.name,
          hostId: values.hostUserID,
          hostFullName: values.hostFullName,
          eventDate: values.eventDate,
          eventLocation: values.location,
          clientIds: values.meetingClientIDs,
          purchaseOrderNbr: values.purchaseOrderNbr || '',
          meetingNotes: values.customNotes || '',
          meetingType: values.meetingType,
          streamUrl: values.streamUrl,
          teamIds: values.meetingTeamIDs,
        };
        await mutationUpdateMeeting({
          variables: {
            data: meetingUpdate,
          },
        }).finally(() => {
          closeMeetingForm();
        });
      }
    },
    [closeMeetingForm, meeting, t, toast, userAccessList, isDuplicateMeeting]
  );

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
      <fieldset disabled={isSubmitting}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          overflowY="auto"
          overflowX="hidden"
          padding={1}
          gap={4}
        >
          <VStack gap={2} w="full" maxW={{ base: 'none', md: '300px' }}>
            <CreateMeetingFormColumnOne
              form={form}
              meeting={meeting}
              setFirstColumnValid={setFirstColumnValid}
              isDuplicateMeeting={isDuplicateMeeting}
            />
          </VStack>
          <Divider
            display={{ base: 'none', md: 'block' }}
            h="auto"
            w="2px"
            orientation="vertical"
          />
          <Box
            as="fieldset"
            disabled={!isColumnOneValid}
            w="full"
            maxW={{ base: 'none', md: '300px' }}
          >
            <VStack gap={2} w="full" opacity={isColumnOneValid ? 1 : 0.5}>
              <CreateMeetingFormColumnTwo
                form={form}
                meeting={meeting}
                isDuplicateMeeting={isDuplicateMeeting}
              />
            </VStack>
          </Box>
        </Flex>

        <Spacer h={4} />

        <Button
          type="submit"
          p="2"
          width="full"
          background="blue.400"
          color="white"
          variant="solid"
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
          aria-label="Submit form"
        >
          {meeting && isDuplicateMeeting && t('common.duplicate')}
          {meeting && !isDuplicateMeeting && t('common.update')}
          {!meeting && t('common.create')}
        </Button>
      </fieldset>
    </form>
  );
};
